import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import { type Timestamp } from "@/types";
import { GraphicDetails } from "@/types/ui";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";

export type CameraSave = {
	x: number;
	y: number;
	zoomLevel: number;
};

export type SettingsStoreState = {
	graphicDetails: GraphicDetails;
	animationsOn: boolean;
	musicOn: boolean;
	soundsOn: boolean;
	musicVolume: number;
	soundsVolume: number;
	showTownLevels: boolean;
	showMainSettlementNameLabel: boolean;
	showConfirmGemsMessages: boolean;
	showConfirmGemsMessagesUpgrades: boolean;
	showConfirmGemsMessagesShop: boolean;
	showIncomePanel: boolean;
	showExplorationSpeed: boolean;
	showGameSavedMessage: boolean;
	showExplorationProgress: boolean;
	showMenuTexts: boolean;
	tileDetailGridView: boolean;
	cameraSave: CameraSave;
	showArrowsToSettlementSwitch: boolean;
};

type UserStore = {
	$getPayload: () => SettingsStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<SettingsStoreState>) => void;
	graphicDetails: ComputedRef<GraphicDetails>;
	animationsOn: ComputedRef<boolean>;
	musicOn: ComputedRef<boolean>;
	soundsOn: ComputedRef<boolean>;
	musicVolume: ComputedRef<number>;
	soundsVolume: ComputedRef<number>;
	showTownLevels: ComputedRef<boolean>;
	showMainSettlementNameLabel: ComputedRef<boolean>;
	showConfirmGemsMessages: ComputedRef<boolean>;
	showConfirmGemsMessagesUpgrades: ComputedRef<boolean>;
	showConfirmGemsMessagesShop: ComputedRef<boolean>;
	showIncomePanel: ComputedRef<boolean>;
	showExplorationSpeed: ComputedRef<boolean>;
	showGameSavedMessage: ComputedRef<boolean>;
	showExplorationProgress: ComputedRef<boolean>;
	showMenuTexts: ComputedRef<boolean>;
	tileDetailGridView: ComputedRef<boolean>;
	cameraSave: ComputedRef<CameraSave>;
	showArrowsToSettlementSwitch: ComputedRef<boolean>;
	setGraphicDetails(details: GraphicDetails): void;
	setAnimations(status: boolean): void;
	setMusic(status: boolean): void;
	setSounds(status: boolean): void;
	setMusicVolume(status: number): void;
	setSoundsVolume(status: number): void;
	setShowTownLevels(status: boolean): void;
	setShowMainSettlementNameLabel(status: boolean): void;
	setShowConfirmGemsMessages(status: boolean): void;
	setShowConfirmGemsMessagesUpgrades(status: boolean): void;
	setShowConfirmGemsMessagesShop(status: boolean): void;
	setShowIncomePanel(status: boolean): void;
	setShowExplorationSpeed(status: boolean): void;
	setShowGameSavedMessage(status: boolean): void;
	setShowExplorationProgress(status: boolean): void;
	setShowMenuTexts(status: boolean): void;
	setTileDetailGridView(value: boolean): void;
	setCameraSave(camera: CameraSave): void;
	setShowArrowsToSettlementSwitch(status: boolean): void;
};

type SettingsStoreDiffAction = {
	// type: "set";
	key: keyof SettingsStoreState;
	currentValue: SettingsStoreState[keyof SettingsStoreState];
	value: SettingsStoreState[keyof SettingsStoreState];
	// reason?: string | false;
	timestamp: Timestamp;
};

type SettingsStorePayload = {
	state: SettingsStoreState;
	actions: Array<SettingsStoreDiffAction>;
};

const STORE_NAME = "settings";

export default defineStore(STORE_NAME, (): UserStore => {
	// Immutable state can never be changed directly,
	// but can be replaced with a new version of the state containing the desired changes.
	const state = createImmutableState<SettingsStoreState>({
		graphicDetails: GraphicDetails.HIGH,
		animationsOn: true,
		musicOn: true,
		soundsOn: true,
		musicVolume: 100,
		soundsVolume: 100,
		showTownLevels: true,
		showMainSettlementNameLabel: true,
		showConfirmGemsMessages: true,
		showConfirmGemsMessagesUpgrades: true,
		showConfirmGemsMessagesShop: true,
		showIncomePanel: true,
		showExplorationSpeed: true,
		showGameSavedMessage: false,
		showExplorationProgress: false,
		showMenuTexts: true,
		tileDetailGridView: false,
		cameraSave: {
			x: (1024 * 256) / 2, // 1024 * 256 je mapa krat tile. V tomto bode ale konstanta v tileRepe este neexistuje
			y: (1024 * 256) / 2,
			zoomLevel: 0.2,
		},
		showArrowsToSettlementSwitch: true,
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): SettingsStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof SettingsStoreState>;
		return keys.reduce(
			(
				acc: SettingsStoreState,
				key: keyof SettingsStoreState,
			): SettingsStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as SettingsStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<SettingsStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<SettingsStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched: boolean = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: SettingsStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): SettingsStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<SettingsStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const graphicDetails = getImmutableStateProperty(
		state,
		"graphicDetails",
		GraphicDetails.HIGH,
	);
	const animationsOn = getImmutableStateProperty(state, "animationsOn", true);
	const musicOn = getImmutableStateProperty(state, "musicOn", true);
	const soundsOn = getImmutableStateProperty(state, "soundsOn", true);
	const musicVolume = getImmutableStateProperty(state, "musicVolume", 100);
	const soundsVolume = getImmutableStateProperty(state, "soundsVolume", 100);

	const showTownLevels = getImmutableStateProperty(
		state,
		"showTownLevels",
		true,
	);

	const showMainSettlementNameLabel = getImmutableStateProperty(
		state,
		"showMainSettlementNameLabel",
		true,
	);

	const showConfirmGemsMessages = getImmutableStateProperty(
		state,
		"showConfirmGemsMessages",
		true,
	);

	const showConfirmGemsMessagesUpgrades = getImmutableStateProperty(
		state,
		"showConfirmGemsMessagesUpgrades",
		true,
	);

	const showConfirmGemsMessagesShop = getImmutableStateProperty(
		state,
		"showConfirmGemsMessagesShop",
		true,
	);

	const showIncomePanel = getImmutableStateProperty(
		state,
		"showIncomePanel",
		true,
	);

	const showExplorationSpeed = getImmutableStateProperty(
		state,
		"showExplorationSpeed",
		true,
	);

	const showGameSavedMessage = getImmutableStateProperty(
		state,
		"showGameSavedMessage",
		true,
	);

	const showExplorationProgress = getImmutableStateProperty(
		state,
		"showExplorationProgress",
		false,
	);

	const showMenuTexts = getImmutableStateProperty(
		state,
		"showMenuTexts",
		true,
	);

	const tileDetailGridView = getImmutableStateProperty(
		state,
		"tileDetailGridView",
		false,
	);

	const showArrowsToSettlementSwitch = getImmutableStateProperty(
		state,
		"showArrowsToSettlementSwitch",
		true,
	);

	const cameraSave = getImmutableStateProperty(state, "cameraSave", {
		x: (1024 * 256) / 2, // 1024 * 256 je mapa krat tile. V tomto bode ale konstanta v tileRepe este neexistuje
		y: (1024 * 256) / 2,
		zoomLevel: 0.2,
	});

	const setGraphicDetails = (details: GraphicDetails): void =>
		setImmutableStateProperty(state, "graphicDetails", details);

	const setAnimations = (status: boolean): void =>
		setImmutableStateProperty(state, "animationsOn", status);

	const setMusic = (status: boolean): void => {
		setImmutableStateProperty(state, "musicOn", status);
		if (status === true) {
			useEmitter().emit(EmitterEvents.PhaserLoadAudio);
			SoundService.getInstance().playMainMusic();
		} else {
			SoundService.getInstance().stopMainMusic();
		}
	};

	const setSounds = (status: boolean): void => {
		setImmutableStateProperty(state, "soundsOn", status);
		if (status === true) {
			useEmitter().emit(EmitterEvents.PhaserLoadAudio);
			SoundService.getInstance().playCityAmbient();
		} else {
			SoundService.getInstance().stopCityAmbient();
		}
	};

	const setMusicVolume = (status: number): void => {
		setImmutableStateProperty(state, "musicVolume", status);
		SoundService.getInstance().changeMainMusicVolume();
	};

	const setSoundsVolume = (status: number): void =>
		setImmutableStateProperty(state, "soundsVolume", status);

	const setShowTownLevels = (status: boolean): void =>
		setImmutableStateProperty(state, "showTownLevels", status);

	const setShowMainSettlementNameLabel = (status: boolean): void =>
		setImmutableStateProperty(state, "showMainSettlementNameLabel", status);

	const setShowConfirmGemsMessages = (status: boolean): void =>
		setImmutableStateProperty(state, "showConfirmGemsMessages", status);

	const setShowConfirmGemsMessagesUpgrades = (status: boolean): void =>
		setImmutableStateProperty(
			state,
			"showConfirmGemsMessagesUpgrades",
			status,
		);

	const setShowConfirmGemsMessagesShop = (status: boolean): void =>
		setImmutableStateProperty(state, "showConfirmGemsMessagesShop", status);

	const setShowIncomePanel = (status: boolean): void =>
		setImmutableStateProperty(state, "showIncomePanel", status);

	const setShowExplorationSpeed = (status: boolean): void =>
		setImmutableStateProperty(state, "showExplorationSpeed", status);

	const setShowGameSavedMessage = (status: boolean): void =>
		setImmutableStateProperty(state, "showGameSavedMessage", status);

	const setShowExplorationProgress = (status: boolean): void =>
		setImmutableStateProperty(state, "showExplorationProgress", status);

	const setShowMenuTexts = (status: boolean): void =>
		setImmutableStateProperty(state, "showMenuTexts", status);

	const setTileDetailGridView = (value: boolean): void =>
		setImmutableStateProperty(state, "tileDetailGridView", value);

	const setCameraSave = (camera: CameraSave): void => {
		setImmutableStateProperty(state, "cameraSave", camera);
	};

	const setShowArrowsToSettlementSwitch = (status: boolean): void => {
		setImmutableStateProperty(
			state,
			"showArrowsToSettlementSwitch",
			status,
		);
	};

	// (Object.keys(state.value) as Array<keyof SettingsStoreState>).forEach(
	// 	(key: keyof SettingsStoreState): void => {
	// 		watch(
	// 			(): SettingsStoreState[keyof SettingsStoreState] =>
	// 				state.value[key],
	// 			(
	// 				value: SettingsStoreState[keyof SettingsStoreState],
	// 				currentValue: SettingsStoreState[keyof SettingsStoreState],
	// 			): void => {
	// 				if (key === "cameraSave") return;
	// 				$actions.push({
	// 					key,
	// 					currentValue,
	// 					value,
	// 					timestamp: Date.now(),
	// 				});
	// 			},
	// 		);
	// 	},
	// );

	return {
		$getPayload,
		$resetPayload,
		$setState,
		graphicDetails,
		animationsOn,
		musicOn,
		soundsOn,
		musicVolume,
		soundsVolume,
		showTownLevels,
		showMainSettlementNameLabel,
		showConfirmGemsMessages,
		showConfirmGemsMessagesUpgrades,
		showConfirmGemsMessagesShop,
		showIncomePanel,
		showExplorationSpeed,
		showGameSavedMessage,
		showExplorationProgress,
		showMenuTexts,
		tileDetailGridView,
		cameraSave,
		showArrowsToSettlementSwitch,
		setCameraSave,
		setGraphicDetails,
		setAnimations,
		setMusic,
		setSounds,
		setMusicVolume,
		setSoundsVolume,
		setShowTownLevels,
		setShowMainSettlementNameLabel,
		setShowConfirmGemsMessages,
		setShowConfirmGemsMessagesUpgrades,
		setShowConfirmGemsMessagesShop,
		setShowIncomePanel,
		setShowExplorationSpeed,
		setShowGameSavedMessage,
		setShowExplorationProgress,
		setShowMenuTexts,
		setTileDetailGridView,
		setShowArrowsToSettlementSwitch,
	};
});
